import { fetch } from '../../utils';

import mixpanel from 'mixpanel-browser';
import * as amplitude from '@amplitude/analytics-browser';
import { Preferences } from '@capacitor/preferences';
import Backend from '../../backend';
import { ga4Tracking, pixelTracking, tracking } from '../../utils/tracking';
import { isCurrentShortSubscriptionTrialing, ShortSubscriptions } from '../../utils/user-subscription';
import NativeOrWeb from '../../utils/native';
import _ from 'lodash';
import { setTrackingUserProps } from '../../utils/tracking/initiate';

//API TO RECREATE A NEWER TOKEN AND EXTEND THE EXP
export const authenticateUser = explicitAccessToken => {
  return fetch(Backend.methodUrl('auth'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(explicitAccessToken ? { accessToken: explicitAccessToken } : {})
  })
    .then(response => {
      // alert(response.status);

      return response.json();
    })
    .then(payload => {
      // alert(JSON.stringify(payload));
      if (payload && payload._id) {
        // identify user if auth success
        mixpanel.identify(payload._id);
        amplitude.setUserId(payload._id);
      }
      return payload;
    })
    .catch(responseError => {
      if (responseError && (responseError.kind || responseError.cancelled)) {
        throw responseError;
      }
      const error = {
        message: 'Authentication failed',
        error: responseError,
        kind: responseError && responseError.status >= 500 ? 'transient' : undefined
      };
      throw error;
    });
};

const trackSuccessfulLogin = data => {
  const campaignCodesText = (data.campaignCodes || []).map(campaignCode => campaignCode.text);
  const hasValidCoreSubscription =
    data.userType === 'Adviser' && !!new ShortSubscriptions(data.subscriptions).containValidCoreSubscription();
  const isTrialing = data.userType === 'Adviser' && isCurrentShortSubscriptionTrialing(data.subscriptions);

  if (data.userType === 'Adviser') {
    mixpanel.identify(data._id);
    mixpanel.people.set({
      $name: data.name,
      $email: data.email,
      $emailVerified: data.emailVerified,
      $created: data.createDate,
      $lastLogin: data.lastLogin,
      $campaignCodesText: campaignCodesText,
      $optOutOfEmail: data.optOutOfEmail,
      $language: data.language,
      $hasValidCoreSubscription: hasValidCoreSubscription,
      $isTrialing: isTrialing,
      $teamName: data.teamDetails.teamNames,
      $teamShortName: data.teamDetails.teamShortNames,
      $teamCampaignCodes: data.teamDetails.teamCampaignCodes,
      $teams: data.teamDetails.teams,
      'User Type': data.userType,
      $gender: data.gender,
      $hobbies: data.hobbies,
      $seniority: data.seniority,
      $personalityTags: data.personalityTags,
      $personality: data.personality,
      $isMDRT: data.isMDRT,
      $age: data.age,
      $dob: data.dob
    });
    mixpanel.register({
      'User Type': data.userType
    });
  }

  amplitude.setUserId(data._id);
  let identify = new amplitude.Identify()
    .set('Name', data.name)
    .set('Email', data.email)
    .set('Email Verified', data.emailVerified)
    .set('User Type', data.userType)
    .set('Last Login', data.lastLogin)
    .set('Campaign Codes Text', campaignCodesText)
    .set('Opt Out Email', data.optOutOfEmail)
    .set('Language', data.language)
    .set('Team Name', data.teamDetails.teamNames)
    .set('Team Short Name', data.teamDetails.teamShortNames)
    .set('Team Campaign Codes', data.teamDetails.teamCampaignCodes)
    .set('Teams', data.teamDetails.teams)
    .set('Seniority', data.seniority)
    .set('Personality Tags', data.personalityTags)
    .set('Personality', data.personality)
    .set('Is MDRT', data.isMDRT)
    .set('Age', data.age)
    .set('DOB', data.dob)
    .set('Gender', data.gender)
    .set('Hobbies', data.hobbies)
    .setOnce('Created', data.createDate);

  if (data.userType === 'Adviser') {
    identify.set('Has Valid Core Subscription', hasValidCoreSubscription);
    identify.set('Is Trialing', isTrialing);
  }
  amplitude.identify(identify);

  if (data.emailVerified) {
    tracking('User Logged In');
  }
};

//API FO LOGIN
export const loginUser = async credentials => {
  try {
    let staySignedIn = !!credentials.staySignedIn;
    if (NativeOrWeb.isNativePlatform()) {
      let loginTimeout = await Preferences.get({ key: 'loginTimeout' });
      if (loginTimeout.value !== 'true') {
        staySignedIn = true;
      }
    }

    const source = NativeOrWeb.isNativePlatform() ? 'app' : 'web';
    const isWechatLogin = !!credentials.wechatCode;
    const isWechatWebLogin = !!credentials.wechatWebCode;
    const isAppleLogin = !!credentials.appleIdToken;
    const isGoogleLogin = !!credentials.googleIdToken || !!credentials.googleAuthCode;
    const url = isWechatLogin
      ? Backend.api('wechatLogin')
      : isWechatWebLogin
      ? Backend.api('wechatWebLogin')
      : isAppleLogin
      ? Backend.api('appleLogin')
      : isGoogleLogin
      ? Backend.api('googleLogin')
      : Backend.methodUrl('loginUser');
    const body = isWechatLogin
      ? { code: credentials.wechatCode, staySignedIn, source }
      : isWechatWebLogin
      ? { code: credentials.wechatWebCode, staySignedIn, source }
      : isAppleLogin
      ? { idToken: credentials.appleIdToken, staySignedIn, source }
      : isGoogleLogin
      ? { idToken: credentials.googleIdToken, googleAuthCode: credentials.googleAuthCode, staySignedIn, source }
      : { ...credentials, source, staySignedIn };

    const res = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(body)
    });
    const payload = await res.json();
    if (payload && payload.success && payload.data && payload.data._id) {
      try {
        trackSuccessfulLogin(payload.data);
      } catch (_) {
        // Login already succeeded — tracking must not become Invalid Email.
      }
    }
    return payload;
  } catch (error) {
    if (error && (error.cancelled || error.kind)) {
      throw error;
    }
    throw {
      message: 'Invalid Email!'
    };
  }
};

export const LOGOUT_REQUEST_TIMEOUT_MS = 8000;

//API TO LOGOUT
export const logoutUser = async _ => {
  let timeoutId;
  let expired = false;
  const {
    beginLogoutRequest,
    peekLogoutRefreshSnapshot,
    isLogoutPending,
    abortInFlightLogout
  } = require('../../utils/auth-refresh');

  if (!isLogoutPending()) {
    throw { message: 'Logout error', cancelled: true };
  }

  try {
    const timedOut = new Promise((_, reject) => {
      timeoutId = setTimeout(() => {
        expired = true;
        abortInFlightLogout();
        reject({ message: 'Logout error' });
      }, LOGOUT_REQUEST_TIMEOUT_MS);
    });

    const attempt = (async () => {
      const refreshToken = peekLogoutRefreshSnapshot();
      const signal = beginLogoutRequest();
      const includeCredentials = !refreshToken;
      let pushToken;
      try {
        pushToken = await NativeOrWeb.getPushNotificationToken();
      } catch (_) {
        pushToken = undefined;
      }
      if (expired) {
        throw { message: 'Logout error' };
      }
      if (!isLogoutPending()) {
        throw { message: 'Logout error', cancelled: true };
      }
      const res = await fetch(
        Backend.methodUrl('logoutUser'),
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            token: pushToken,
            ...(refreshToken ? { refreshToken } : {})
          }),
          signal
        },
        includeCredentials
      );
      if (expired) {
        throw { message: 'Logout error' };
      }
      const payload = await res.json();
      if (expired) {
        throw { message: 'Logout error' };
      }
      if (!res.ok || !payload || payload.success !== true) {
        throw { message: 'Logout error' };
      }
      return payload;
    })();

    return await Promise.race([attempt, timedOut]);
  } catch (error) {
    if (error && error.cancelled) {
      throw error;
    }
    throw { message: 'Logout error' };
  } finally {
    expired = true;
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
  }
};

//API TO CREATE THE USER FOR ADVISER
const withRegisterSource = data => ({
  ...data,
  source: data && data.source ? data.source : NativeOrWeb.isNativePlatform() ? 'app' : 'web'
});

export const createUser = data => {
  return fetch(Backend.methodUrl('registerUser'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(withRegisterSource(data))
  })
    .then(res => {
      return res.json();
    })
    .then(payload => {
      if (payload && payload.success && payload.data && payload.data._id) {
        if (payload.data.userType === 'Adviser') {
          // Tracking
          mixpanel.identify(payload.data._id);
          mixpanel.people.set({
            $name: payload.data.name,
            $email: payload.data.email,
            'User Type': payload.data.userType,
            $created: payload.data.createDate
          });
          // pixelTracking('CompleteRegistration');
        }

        // Tracking
        let identify = new amplitude.Identify()
          .set('Name', payload.data.name)
          .set('Email', payload.data.email)
          .set('Email Verified', payload.data.emailVerified)
          .set('User Type', payload.data.userType)
          .setOnce('Created', payload.data.createDate);
        amplitude.identify(identify);

        tracking('User Created');

        ga4Tracking('user_signup', {
          user_type: payload.data.userType,
          status: 'accepted',
          phone: payload.data.phoneNumber,
          email: payload.data.email,
          username: payload.data.name,
          opt_in: !payload.data.optOutOfEmail,
          language: payload.data.language
        });
      }

      return payload;
    })
    .catch(error => {
      throw error;
    });
};

//API TO CREATE THE USER FOR CLIENT
export const createClient = data => {
  return fetch(Backend.methodUrl('registerClient'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(withRegisterSource(data))
  })
    .then(res => {
      return res.json();
    })
    .then(payload => {
      if (payload && payload.success && payload.data && payload.data._id) {
        // Tracking
        amplitude.setUserId(payload.data._id);
        let identify = new amplitude.Identify()
          .set('Name', payload.data.name)
          .set('Email', payload.data.email)
          .set('Email Verified', payload.data.emailVerified)
          .set('User Type', payload.data.userType)
          .setOnce('Created', payload.data.createDate);
        amplitude.identify(identify);

        tracking('User Created');

        // pixelTracking('Lead');
      }

      return payload;
    })
    .catch(error => {
      throw error;
    });
};

//API TO APPROVE THE USER BY SUPERADMIN
export const approveUser = data => {
  return fetch(Backend.methodUrl('approveUser'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(data)
  })
    .then(res => {
      return res.json();
    })
    .then(payload => {
      return payload;
    })
    .catch(error => {
      throw error;
    });
};

//API TO VERFIY THE EMAIL OF ADVISER
export const verifyEmail = data => {
  return fetch(Backend.methodUrl('verifyEmail'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(data)
  })
    .then(res => {
      return res.json();
    })
    .then(payload => {
      return payload;
    })
    .catch(error => {
      throw error;
    });
};

//API TO UPDATE THE USER PROFILE
export const updateUserDetails = data => {
  return fetch(Backend.methodUrl('updateUserDetails'), {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(data)
  })
    .then(res => {
      return res.json();
    })
    .then(payload => {
      setTrackingUserProps(payload.data._id, payload.data);
      return payload;
    })
    .catch(error => {
      throw error;
    });
};

//API TO CHANGE THE PASSWORD
export const changePassword = data => {
  return fetch(Backend.methodUrl('changePassword'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(data)
  })
    .then(res => {
      return res.json();
    })
    .then(payload => {
      return payload;
    })
    .catch(error => {
      throw error;
    });
};

//API TO GET THE LINK OF RESET PASSWORD
export const forgotPassword = data => {
  return fetch(Backend.methodUrl('forgotPassword'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(data)
  })
    .then(res => {
      return res.json();
    })
    .then(payload => {
      return payload;
    })
    .catch(error => {
      throw error;
    });
};

//API TO GET THE RESET PASSWORD WHEN USER GO TO THORUGH THE LINK
export const resetPassword = data => {
  return fetch(Backend.methodUrl('resetPassword'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(data)
  })
    .then(res => {
      return res.json();
    })
    .then(payload => {
      return payload;
    })
    .catch(error => {
      throw error;
    });
};

//API TO GET THE LOGIN USER DATA
export const getUser = () => {
  return fetch(Backend.methodUrl('getUser'), {
    method: 'GET'
  })
    .then(res => {
      return res.json();
    })
    .then(payload => {
      setTrackingUserProps(payload.data._id, payload.data);
      return payload;
    })
    .catch(error => {
      throw error;
    });
};

//API TO GET THE CONNECTED ADVISER FOR CHAT WITH ADVISER
export const getConnectedAdviser = () => {
  return fetch(Backend.methodUrl('getConnectedAdviser'), {
    method: 'GET'
  })
    .then(res => {
      return res.json();
    })
    .then(payload => {
      return payload;
    })
    .catch(error => {
      throw error;
    });
};

//API TO GET THE FOREX CURRENCY RATE
export const currencyExchangeRate = () => {
  return fetch(Backend.methodUrl('currencyExchangeRate'), {
    method: 'GET'
  })
    .then(res => {
      return res.json();
    })
    .then(payload => {
      return payload;
    })
    .catch(error => {
      throw error;
    });
};

//API TO GET THE DEFAULT MESSAGE
export const defaultMessages = () => {
  return fetch(Backend.methodUrl('defaultMessages'), {
    method: 'GET'
  })
    .then(res => {
      return res.json();
    })
    .then(payload => {
      return payload;
    })
    .catch(error => {
      throw error;
    });
};

//API TO GET THE NOTIFICATIUON OF REMINDER POLICY
export const getNotifications = () => {
  return fetch(Backend.methodUrl('getNotifications'), {
    method: 'GET'
  })
    .then(res => {
      return res.json();
    })
    .then(payload => {
      return payload;
    })
    .catch(error => {
      throw error;
    });
};

//API TO GET THE FIGURE USER
export const getFigureUser = () => {
  return fetch(Backend.methodUrl('getFigureUser'), {
    method: 'GET'
  })
    .then(res => {
      return res.json();
    })
    .then(payload => {
      return payload;
    })
    .catch(error => {
      throw error;
    });
};

// APT TO CHECK WHETHER EMAIL IS REGISTERED
export const checkEmail = email => {
  return fetch(Backend.methodUrl(`check-email/${email}`), {
    method: 'GET'
  })
    .then(res => {
      return res.json();
    })
    .then(payload => {
      if (payload.error) {
        throw new Error(payload.error);
      } else {
        return payload;
      }
    })
    .catch(error => {
      throw error;
    });
};

// APT TO CHECK WHETHER EMAIL IS REGISTERED
export const requestAccountDeletion = (email, survey) => {
  return fetch(Backend.api(`account/deletion`), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ email: email, survey: survey })
  })
    .then(res => {
      return res.json();
    })
    .then(payload => {
      if (payload.error) {
        throw new Error(payload.error);
      } else {
        if (payload.message === 'Requested') {
          // disable the deleted button
          localStorage.setItem('accountDeletionRequested', 'true');
        }
        return payload;
      }
    })
    .catch(error => {
      throw error;
    });
};

export const addCampaignCode = data => {
  return fetch(Backend.methodUrl('addCampaignCode'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(data)
  })
    .then(res => {
      return res.json();
    })
    .then(payload => {
      return payload;
    })
    .catch(error => {
      throw error;
    });
};

//API TO REQUEST ONE-TIME CODE FOR EMAIL LOGIN
export const requestOneTimeCode = (email, language) => {
  return fetch(Backend.methodUrl('requestOneTimeCode'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ email, language })
  })
    .then(res => {
      return res.json();
    })
    .then(payload => {
      return payload;
    })
    .catch(error => {
      throw error;
    });
};
